import { useState } from "react";
import { IconDeviceFloppy, IconInfoCircle, IconX } from "@tabler/icons-react";
import { Alert, Button, Card, Divider, Group, Heading, Input, NumberInput, Stack, Textarea } from "@/components/ui";
import { toastError } from "@/lib/toast";
import type { ContratoAnualizadoAdmin, ContratoAnualizadoInput } from "@/lib/types";
import { ConceptosEditor } from "@/features/admin-compras-shared/ConceptosEditor";
import { ProveedorFields, proveedorVacio } from "@/features/admin-compras-shared/ProveedorFields";
import { useActualizarContrato, useCrearContrato, useUsoContrato } from "../hooks/use-contratos";

function contratoAInput(ca: ContratoAnualizadoAdmin): ContratoAnualizadoInput {
  return {
    division: ca.division,
    estatus: ca.estatus,
    fecha: ca.fecha,
    subTotal: ca.subTotal,
    impuestos: ca.impuestos,
    total: ca.total,
    moneda: ca.moneda,
    observaciones: ca.observaciones,
    proveedor: { ...ca.proveedor },
    conceptos: ca.conceptos.map((c) => ({
      lineaPedido: c.lineaPedido, cantidad: c.cantidad, unidad: c.unidad,
      noIdentificacion: c.noIdentificacion, descripcion: c.descripcion,
      valorUnitario: c.valorUnitario, importe: c.importe, observaciones: c.observaciones,
      centroCostos: c.centroCostos, descCecos: c.descCecos, cuentaContable: c.cuentaContable,
      impuestos: c.impuestos.map((x) => ({ tipo: x.tipo, indicador: x.indicador, monto: x.monto, porcentaje: x.porcentaje })),
    })),
  };
}

function inputVacio(): ContratoAnualizadoInput {
  return {
    division: null, estatus: "1", fecha: null, subTotal: 0, impuestos: 0, total: 0,
    moneda: "MXN", observaciones: null, proveedor: proveedorVacio(), conceptos: [],
  };
}

interface Props {
  inicial: ContratoAnualizadoAdmin | null;
  onDone: () => void;
}

export function ContratoForm({ inicial, onDone }: Props) {
  const esNuevo = inicial === null;
  const [clave, setClave] = useState(inicial?.clave ?? "");
  const [form, setForm] = useState<ContratoAnualizadoInput>(inicial ? contratoAInput(inicial) : inputVacio());

  const crear = useCrearContrato({ onOk: onDone });
  const actualizar = useActualizarContrato({ onOk: onDone });
  const guardando = crear.isPending || actualizar.isPending;

  // Aviso (no bloquea): al editar un CA ya referenciado, el cambio solo aplica a solicitudes nuevas.
  const { data: uso } = useUsoContrato(esNuevo ? null : (inicial?.clave ?? null));
  const relacionadas = uso?.solicitudesRelacionadas ?? 0;

  const upd = (patch: Partial<ContratoAnualizadoInput>) => setForm((f) => ({ ...f, ...patch }));

  const submit = () => {
    if (!clave.trim()) {
      toastError({ title: "Falta la clave", message: "La clave del contrato es obligatoria." });
      return;
    }
    if (!form.proveedor.nombre.trim() || !form.proveedor.rfc.trim()) {
      toastError({ title: "Proveedor incompleto", message: "Nombre y RFC del proveedor son obligatorios." });
      return;
    }
    if (esNuevo) crear.mutate({ clave: clave.trim(), input: form });
    else actualizar.mutate({ clave: clave.trim(), input: form });
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Heading order={5}>{esNuevo ? "Nuevo contrato anualizado" : `Editar contrato ${inicial?.clave}`}</Heading>

        {relacionadas > 0 && (
          <Alert variant="warning" icon={<IconInfoCircle size={18} />} title="Contrato con solicitudes relacionadas">
            {relacionadas} solicitud(es) de pago ya referencian este contrato
            {uso && uso.solicitudesVigentes > 0 ? ` (${uso.solicitudesVigentes} vigente(s))` : ""}. Tus cambios
            (proveedor, total, moneda, conceptos) <strong>solo aplicarán a solicitudes nuevas</strong>: las
            existentes conservan la copia que tomaron al asociar el contrato.
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Input label="Clave" required readOnly={!esNuevo} value={clave} onChange={(e) => setClave(e.currentTarget.value)} className="font-mono" />
          <Input label="División" value={form.division ?? ""} onChange={(e) => upd({ division: e.currentTarget.value || null })} />
          <Input label="Estatus" value={form.estatus ?? ""} onChange={(e) => upd({ estatus: e.currentTarget.value || null })} />
          <Input label="Fecha (ISO, opcional)" placeholder="2026-01-01" value={form.fecha ?? ""} onChange={(e) => upd({ fecha: e.currentTarget.value || null })} />
          <Input label="Moneda" required value={form.moneda} onChange={(e) => upd({ moneda: e.currentTarget.value })} />
          <NumberInput label="Subtotal" hideControls decimals={4} value={form.subTotal} onChange={(v) => upd({ subTotal: Number(v) || 0 })} />
          <NumberInput label="Impuestos" hideControls decimals={4} value={form.impuestos} onChange={(v) => upd({ impuestos: Number(v) || 0 })} />
          <NumberInput label="Total" hideControls decimals={4} value={form.total} onChange={(v) => upd({ total: Number(v) || 0 })} />
        </div>

        <ProveedorFields value={form.proveedor} onChange={(p) => upd({ proveedor: p })} />

        <Textarea label="Observaciones" rows={1} value={form.observaciones ?? ""} onChange={(e) => upd({ observaciones: e.currentTarget.value || null })} />

        <Divider label="Conceptos" />
        <ConceptosEditor value={form.conceptos} onChange={(cs) => upd({ conceptos: cs })} readOnly />

        <Group justify="flex-end">
          <Button variant="default" leftSection={<IconX size={14} />} onClick={onDone}>Cancelar</Button>
          <Button leftSection={<IconDeviceFloppy size={14} />} loading={guardando} onClick={submit}>
            {esNuevo ? "Crear contrato" : "Guardar cambios"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
